// Ground truth for the water level: photos a person has labelled with the real level, compared with
// what the sensor reported. Pure - no React, no fetching.
//
// A label is stored as the raw ruler reading (true_level_cm), but charts and comparisons use the
// backend's true_level_field_cm: the same level with the ground-level mark (30 cm) as 0, so below
// ground is negative - the scale PadiHAT's own water level is on.

// A sensor reading counts as "at the time of a label" only if it is this close; further away it says
// nothing about that moment (the level may have changed since).
export const MAX_MATCH_MS = 5 * 60 * 1000;

// Only PadiHAT is compared - its own level (water_level_cm, the derived level - not d1_cal, which is a distance) and
// the level from our range estimate (water_level_est_cm, offset - d1_est): the pressure sensor reports depth, not
// field level, so it is not on this scale. A sensor with no reading near any label is left out.
export const SENSOR_FIELDS = [
  { key: "water_level_cm", label: "PadiHAT" },
  { key: "water_level_est_cm", label: "PadiHAT (estimated)" },
];

// The ruler range a label may take, and the ground-level mark on it (mirrors the backend).
export const RULER_MAX_CM = 45;
export const GROUND_LEVEL_RULER_CM = 30;
export const fieldLevel = (rulerCm) => Math.round((rulerCm - GROUND_LEVEL_RULER_CM) * 100) / 100;

export const imageTime = (image) => new Date(image.captured_at || image.server_received_at).getTime();

// The Radar Lab's ruler reader reads every photo of the greenhouse camera in the background (backend
// `ruler_reading`). Its reading is used as the photo's label when no person has labelled it: at this confidence or
// more it counts as truth (green); below it, it waits for a person to accept or correct it (yellow) and counts for
// nothing until then. Mirrors the backend's experiment rules.LIMITS['truth_confidence_min'] (0.9 since 21 Oct 2026; was
// 0.95 here): change both together. A campaign never stops for a reading below it; the photo waits for review.
export const READER_TRUST_CONFIDENCE = 0.9;

const isNumber = (value) => typeof value === "number" && !Number.isNaN(value);

/**
 * What a photo's ground truth is, or null when it has none:
 * - a person's label: {source: "human", fieldCm, rulerCm, counts: true} - always wins;
 * - else a ruler reading at READER_TRUST_CONFIDENCE or more: {source: "reader", fieldCm, rulerCm, confidence, counts: true};
 * - else a lower-confidence reading: the same with review: true and counts: false (not truth until a person reviews it).
 * A refused reading (no level) or a photo the reader has not read is null.
 */
export const truthOf = (image) => {
  if (!image || Number.isNaN(imageTime(image))) return null;
  if (isNumber(image.true_level_field_cm)) {
    return { source: "human", fieldCm: image.true_level_field_cm, rulerCm: image.true_level_cm, counts: true, review: false };
  }
  const reading = image.ruler_reading;
  if (!reading || !isNumber(reading.level_cm) || !isNumber(reading.field_level_cm)) return null;
  const confidence = isNumber(reading.confidence) ? reading.confidence : 0;
  const counts = confidence >= READER_TRUST_CONFIDENCE;
  return { source: "reader", fieldCm: reading.field_level_cm, rulerCm: reading.level_cm, confidence, counts, review: !counts };
};

export const isLabelled = (image) => Boolean(truthOf(image)?.counts);
// The truth (field level, cm) of a labelled photo; only call it on photos isLabelled accepts.
export const truthFieldCm = (image) => truthOf(image)?.fieldCm;

// One line for a photo's label, saying where it came from ("" when it has none): the chart marker's tooltip.
export const truthText = (image) => {
  const truth = truthOf(image);
  if (!truth) return "";
  if (truth.source === "human") return `✓ ${truth.rulerCm} cm · labelled${image.labeled_by ? ` by ${image.labeled_by}` : ""}`;
  const confidence = truth.confidence.toFixed(2);
  return truth.counts ? `✓ ${truth.rulerCm} cm · ruler reader ${confidence}` : `⚠ check: ruler reader ${truth.rulerCm} cm (${confidence})`;
};

// One chart row per labelled photo, at the time it was taken.
export const buildTruthRows = (images) =>
  (images || []).filter(isLabelled).map((image) => ({ timeNumeric: imageTime(image), truth_level_cm: truthFieldCm(image) }));

// Adds the labels to the chart's rows as their own points (the two never share a timestamp, so they
// only ever draw the truth line), keeping time order.
export const withTruthRows = (rows, images) => {
  const truth = buildTruthRows(images);
  if (truth.length === 0) return rows || [];
  return [...(rows || []), ...truth].sort((a, b) => a.timeNumeric - b.timeNumeric);
};

// The reading of `field` nearest to `time`, within maxMs, or undefined.
export const nearestReading = (rows, field, time, maxMs) => {
  let best;
  let bestDelta = Infinity;
  for (const row of rows) {
    const value = row[field];
    if (typeof value !== "number") continue;
    const delta = Math.abs(row.timeNumeric - time);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = value;
    }
  }
  return bestDelta <= maxMs ? best : undefined;
};

const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};
const round2 = (value) => Math.round(value * 100) / 100;
// Sample standard deviation (null for one value): how steady the offset is. A small spread with a large mean
// means a constant offset removes it; a large spread means no single offset will.
const spreadOf = (values) => {
  if (values.length < 2) return null;
  const m = values.reduce((sum, v) => sum + v, 0) / values.length;
  return Math.sqrt(values.reduce((sum, v) => sum + (v - m) ** 2, 0) / (values.length - 1));
};

/**
 * How far each sensor is from the labelled truth: offset = sensor - truth (positive = reads high),
 * using the sensor reading nearest in time to each label (within maxMs). Returns
 * [{key, label, count, mean, median, sd, maxAbs}] for the sensors that have at least one match.
 */
export const summarizeOffsets = (rows, images, { maxMs = MAX_MATCH_MS } = {}) => {
  const labelled = (images || []).filter(isLabelled);
  return SENSOR_FIELDS.map(({ key, label }) => {
    const offsets = [];
    for (const image of labelled) {
      const reading = nearestReading(rows || [], key, imageTime(image), maxMs);
      if (reading !== undefined) offsets.push(reading - truthFieldCm(image));
    }
    if (offsets.length === 0) return null;
    return {
      key,
      label,
      count: offsets.length,
      mean: round2(offsets.reduce((sum, value) => sum + value, 0) / offsets.length),
      median: round2(median(offsets)),
      sd: spreadOf(offsets) === null ? null : round2(spreadOf(offsets)),
      maxAbs: round2(Math.max(...offsets.map(Math.abs))),
    };
  }).filter(Boolean);
};

export const countLabelled = (images) => (images || []).filter(isLabelled).length;

// How the run's photos are labelled: by a person, by the ruler reader (trusted), waiting for review, or refused
// by the reader. A photo the reader has not read yet and no person has labelled is in none of these.
export const labelSummary = (images) => {
  const summary = { human: 0, reader: 0, review: 0, refused: 0 };
  for (const image of images || []) {
    const truth = truthOf(image);
    if (truth?.source === "human") summary.human += 1;
    else if (truth?.counts) summary.reader += 1;
    else if (truth?.review) summary.review += 1;
    else if (image?.ruler_reading && !isNumber(image.ruler_reading.level_cm)) summary.refused += 1;
  }
  return summary;
};

// The oldest photo waiting for review (a lower-confidence reading no person has labelled yet), or null.
export const nextToReview = (images) =>
  (images || []).filter((image) => truthOf(image)?.review).sort((a, b) => imageTime(a) - imageTime(b))[0] || null;

/**
 * How the ruler reader agrees with a person on the photos that have both: {count, meanMm, maxAbsMm} (reader minus
 * person, in mm; positive = the reader reads higher), or null when no photo has both. The reader was tuned on a
 * teammate's labels, so a steady offset here means the two labelling conventions differ.
 */
export const readerAgreement = (images) => {
  const diffs = [];
  for (const image of images || []) {
    const reading = image?.ruler_reading;
    if (isNumber(image?.true_level_cm) && reading && isNumber(reading.level_cm)) diffs.push((reading.level_cm - image.true_level_cm) * 10);
  }
  if (diffs.length === 0) return null;
  const round1 = (value) => Math.round(value * 10) / 10;
  return {
    count: diffs.length,
    meanMm: round1(diffs.reduce((sum, value) => sum + value, 0) / diffs.length),
    maxAbsMm: round1(Math.max(...diffs.map(Math.abs))),
  };
};
